import { Suspense, useEffect, useRef } from 'react';
import { useParams } from 'react-router';

import { useApplyBranding } from '@/hooks/useApplyBranding';
import { useFocusOnChange } from '@/hooks/useFocusOnChange';
import { BareLayout } from '@/layouts/BareLayout';
import { CashierLayout } from '@/layouts/CashierLayout';
import { lazyWithRetry } from '@/lib/lazyWithRetry';
import {
  ExpiredPage,
  InvalidLinkPage,
  ResolvingPage,
  SessionErrorPage,
  type ViewState,
} from '@/modules/session';
import {
  fetchMethods,
  resolveSession,
  selectViewState,
  useSessionDispatch,
  useSessionState,
} from '@/state';

const MethodSelectPage = lazyWithRetry(
  () => import('@/modules/methods').then((m) => ({ default: m.MethodSelectPage })),
  'methods',
);
const PayerWalletPage = lazyWithRetry(
  () => import('@/modules/methods').then((m) => ({ default: m.PayerWalletPage })),
  'methods',
);
const UnavailablePage = lazyWithRetry(
  () => import('@/modules/methods').then((m) => ({ default: m.UnavailablePage })),
  'methods',
);
const ProcessingPage = lazyWithRetry(
  () => import('@/modules/payment').then((m) => ({ default: m.ProcessingPage })),
  'payment',
);
const WalletInstructionsPage = lazyWithRetry(
  () => import('@/modules/proof').then((m) => ({ default: m.WalletInstructionsPage })),
  'proof',
);
const SubmitProofPage = lazyWithRetry(
  () => import('@/modules/proof').then((m) => ({ default: m.SubmitProofPage })),
  'proof',
);
const UnderReviewPage = lazyWithRetry(
  () => import('@/modules/proof').then((m) => ({ default: m.UnderReviewPage })),
  'proof',
);
const SucceededPage = lazyWithRetry(
  () => import('@/modules/outcome').then((m) => ({ default: m.SucceededPage })),
  'outcome',
);
const FailedPage = lazyWithRetry(
  () => import('@/modules/outcome').then((m) => ({ default: m.FailedPage })),
  'outcome',
);

function decodeTokenParam(raw: string | undefined): string | null {
  if (raw === undefined || raw === '') return null;
  try {
    return decodeURIComponent(raw);
  } catch {
    return null;
  }
}

function ViewBody({ viewState }: { viewState: ViewState }) {
  switch (viewState) {
    case 'resolving':
      return <ResolvingPage />;
    case 'invalidLink':
      return <InvalidLinkPage />;
    case 'unavailable':
      return <UnavailablePage />;
    case 'selectMethod':
      return <MethodSelectPage />;
    case 'collectPayerWallet':
      return <PayerWalletPage />;
    case 'walletInstructions':
      return <WalletInstructionsPage />;
    case 'submitProof':
      return <SubmitProofPage />;
    case 'processing':
      return <ProcessingPage />;
    case 'underReview':
      return <UnderReviewPage />;
    case 'succeeded':
      return <SucceededPage />;
    case 'failed':
      return <FailedPage />;
    case 'expired':
      return <ExpiredPage />;
    case 'error':
      return <SessionErrorPage />;
    default: {
      const _never: never = viewState;
      return _never;
    }
  }
}

export function CashierSessionPage() {
  const params = useParams();
  const token = decodeTokenParam(params['token']);
  const dispatch = useSessionDispatch();
  const state = useSessionState();
  const viewState = selectViewState(state);

  useApplyBranding(state.branding, state.brandingApplied, dispatch);
  useFocusOnChange(viewState);

  const tilesLoadStateRef = useRef(state.tilesLoadState);
  tilesLoadStateRef.current = state.tilesLoadState;

  useEffect(() => {
    if (token === null) return;
    const controller = new AbortController();
    void resolveSession(dispatch, token, controller.signal);
    return () => {
      controller.abort();
    };
  }, [dispatch, token]);

  useEffect(() => {
    if (token === null) return;
    if (viewState !== 'selectMethod') return;
    // Start-gate only. `tilesLoadState` must not be an effect dependency: METHODS_LOAD_STARTED
    // would abort this GET (Chrome: (canceled)) before the catalogue arrives.
    const load = tilesLoadStateRef.current;
    if (load === 'loaded' || load === 'error') return;
    const controller = new AbortController();
    void fetchMethods(dispatch, token, controller.signal);
    return () => {
      controller.abort();
    };
  }, [dispatch, token, viewState]);

  if (viewState === 'invalidLink' || token === null) {
    return (
      <BareLayout>
        <InvalidLinkPage />
      </BareLayout>
    );
  }

  return (
    <CashierLayout>
      <Suspense fallback={<ResolvingPage />}>
        <ViewBody viewState={viewState} />
      </Suspense>
    </CashierLayout>
  );
}
